import { useReactiveVar } from "@apollo/client";
import { Dialog } from "@headlessui/react";
import { PropsWithChildren, useCallback, useEffect } from "react";

import Notification from "@apps/main/components/notification";
import { isConfirmationOpenVar, isSidebarOpened } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import { toObjectProps } from "@components/link/link";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

export const SidebarHeader = ({ children }: PropsWithChildren<{}>) => {
  return (
    <div className="mx-6 my-4 font-medium text-gray-900 text-lg">
      {children}
    </div>
  );
};

const Sidebar = ({
  children,
  title,
  expandToUrl,
  wide,
  className = "",
  headerClassName = "",
  additionalActions,
  fullHeight,
  onClose,
}: PropsWithChildren<{
  title?: string;
  wide?: boolean;
  expandToUrl?: string | toObjectProps;
  className?: string;
  headerClassName?: string;
  additionalActions?: JSX.Element;
  fullHeight?: boolean;
  onClose: () => void;
}>) => {
  const isConfirmationOpen = useReactiveVar(isConfirmationOpenVar);
  const isSidebarOpenedCached = useReactiveVar(isSidebarOpened);

  const handleClose = useCallback(() => {
    // if the confirmation modal is open, we don't want to close the sidebar
    if (!isConfirmationOpen) {
      onClose();
      isSidebarOpened(false);
    }
  }, [isConfirmationOpen, onClose]);

  useEffect(() => {
    isSidebarOpened(true);
    return () => {
      isSidebarOpened(false);
    };
  }, []);

  return (
    <Dialog
      as="div"
      open
      className="fixed inset-0 overflow-hidden z-rightSidebar"
      onClose={handleClose}
    >
      {/* Rendering the notification in the sidebar prevents closing the sidebar when trying to dismiss the notification */}
      {isSidebarOpenedCached && <Notification />}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-black opacity-20"
          onClick={handleClose}
        />

        <div className="fixed inset-y-0 right-0 max-w-full flex">
          <div
            className={classNames(
              "w-screen",
              !wide && "max-w-2xl",
              wide && "max-w-5xl"
            )}
          >
            <div
              className={classNames(
                "h-full flex flex-col bg-white shadow-xl overflow-y-scroll",
                className
              )}
            >
              <div
                className={classNames(
                  "pl-[18px] pr-6 py-3 sticky top-0 z-1 bg-white",
                  headerClassName
                )}
              >
                <div className="flex items-center justify-between gap-2 ">
                  <div className="flex items-center gap-2 flex-1">
                    <Button
                      icon={getTopicflowIcon("chevronRightDouble")}
                      theme={buttonTheme.iconGray}
                      onClick={handleClose}
                      aria-label="Close"
                    />
                    {expandToUrl && (
                      <Button
                        to={expandToUrl}
                        icon={getTopicflowIcon("expandArrows")}
                        theme={buttonTheme.iconGray}
                        onClick={handleClose}
                      />
                    )}
                  </div>
                  {additionalActions && (
                    <div className="flex items-center gap-2">
                      {additionalActions}
                    </div>
                  )}
                </div>
              </div>
              {title && (
                <div className="px-6 pb-4 font-medium text-gray-900 text-lg">
                  {title}
                </div>
              )}
              <div className="relative flex-1">
                <div className="absolute inset-0">
                  <div className="h-full">
                    {/* pb-12 is to leave space for the Intercom button so there are no overlaps */}
                    <div
                      className={classNames(
                        fullHeight && "h-full flex flex-col",
                        !fullHeight && "pb-12"
                      )}
                    >
                      {children}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default Sidebar;
